import { useCallback, useEffect, useRef, useState } from 'react';

// Feather-style inline icons (no emojis — see project guidelines).
const PlayIcon = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>
);
const PauseIcon = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" />
  </svg>
);
const SoundOnIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" /><path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
  </svg>
);
const SoundOffIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <line x1="23" y1="9" x2="17" y2="15" /><line x1="17" y1="9" x2="23" y2="15" />
  </svg>
);

// Only one video may play at a time across the page — whoever starts playing
// claims the slot and the previous holder is paused. Pausing an element fires
// its own `pause` event, so each player's UI stays in sync on its own.
let activeVideo = null;
const claimPlayback = (el) => {
  if (activeVideo && activeVideo !== el) { try { activeVideo.pause(); } catch { /* detached */ } }
  activeVideo = el;
};
const releasePlayback = (el) => { if (activeVideo === el) activeVideo = null; };

// Poster + tap-to-play <video> with a sound on/off toggle. Clicking the frame
// toggles play/pause and flashes a play/pause glyph; scrolling the video out of
// the viewport stops it. Stops click propagation so playing inside a clickable
// feed card doesn't also open the post modal. Pass className="video-media--contain"
// to letterbox (post modal).
export default function VideoMedia({ src, poster, className = '' }) {
  const ref = useRef(null);
  const [started, setStarted] = useState(false);
  const [paused, setPaused] = useState(true);
  const [muted, setMuted] = useState(false);
  // { icon, n } — n restarts the CSS fade when the same glyph flashes twice.
  const [hint, setHint] = useState(null);
  const hintTimer = useRef(null);
  const hintSeq = useRef(0);

  useEffect(() => () => clearTimeout(hintTimer.current), []);

  // Give the slot back if this player is unmounted mid-playback (modal close,
  // feed re-render) so the next video isn't blocked by a dead element.
  useEffect(() => {
    const el = ref.current;
    return () => releasePlayback(el);
  }, []);

  // Scrolled mostly out of view: stop. Deliberately never auto-resumes on the
  // way back — starting playback stays a click.
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.intersectionRatio < 0.5 && !el.paused) el.pause(); },
      { threshold: [0, 0.5, 1] },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Only ever flashed from a click — a pause caused by scrolling away or by
  // another video claiming the slot shouldn't draw attention to itself.
  const flashHint = useCallback((icon) => {
    clearTimeout(hintTimer.current);
    hintSeq.current += 1;
    setHint({ icon, n: hintSeq.current });
    hintTimer.current = setTimeout(() => setHint(null), 700);
  }, []);

  const start = (e) => {
    e.stopPropagation();
    const v = ref.current;
    if (!v) return;
    // Unmute on the very first play only — a later resume via this same button
    // must respect whatever the user set with the sound toggle.
    if (!started) { v.muted = false; setMuted(false); }
    v.play().catch(() => {});
  };
  const togglePlay = (e) => {
    e.stopPropagation();
    const v = ref.current;
    if (!v) return;
    if (v.paused) { v.play().catch(() => {}); flashHint('play'); }
    else { v.pause(); flashHint('pause'); }
  };
  const toggleMute = (e) => {
    e.stopPropagation();
    const v = ref.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  return (
    <div className={`video-media ${className}`} onClick={(e) => e.stopPropagation()}>
      <video
        ref={ref}
        src={src}
        poster={poster || undefined}
        className="video-media-el"
        playsInline
        loop
        onClick={togglePlay}
        onPlay={() => { claimPlayback(ref.current); setStarted(true); setPaused(false); }}
        onPause={() => { releasePlayback(ref.current); setPaused(true); }}
      />
      {hint && (
        <div className="video-media-hint" key={hint.n} aria-hidden="true">
          {hint.icon === 'play' ? <PlayIcon size={20} /> : <PauseIcon size={20} />}
        </div>
      )}
      {/* Once the click's glyph has faded, a still-paused video keeps a play
          affordance — it may have been stopped by a scroll or by another video
          taking over, with no click to explain it. */}
      {paused && !hint && (
        <button className="video-media-play" onClick={start} aria-label="Play video">
          <PlayIcon />
        </button>
      )}
      {started && (
        <button className="video-media-mute" onClick={toggleMute} aria-label={muted ? 'Unmute' : 'Mute'}>
          {muted ? <SoundOffIcon /> : <SoundOnIcon />}
        </button>
      )}
    </div>
  );
}
